import React from 'react';
import Button from './Button';
import OverflowMenu from './OverflowMenu';
import { useIsMobile } from '../contexts/ViewportContext';
import type { WriteCapabilityLevel } from '../contexts/WriteGuardContext';
import { Icon } from './Icon';
import type { IconName } from './Icon';

/**
 * The page's primary "create" action(s).
 *
 * Desktop renders exactly what the app rendered before: one labelled primary
 * `Button` per action. Mobile collapses them — a single action becomes a round
 * icon button, two or more become a round `+` that opens a menu.
 *
 * WHY A HOOK AND NOT CSS: the CSS route would render both variants and hide
 * one, which duplicates the `id` attributes. Seven of these buttons are
 * onboarding-tour anchors resolved with `getElementById`, so the tour would
 * silently grab the hidden node. See Standards.md §20.5.
 *
 * The action list is a data object because two pages already model it that way
 * — tenant Inventory and admin Catálogo compute their create action from the
 * active tab. This just gives that shape a name.
 *
 * An empty `actions` array renders nothing, which is what tab-conditional
 * pages need (`actions={tab === 'x' ? [...] : []}`).
 */

export interface CreateAction {
    label: string;
    /** Material Symbols ligature. */
    icon: IconName;
    onClick: () => void;
    /**
     * Role gate. Deliberately has NO default: several pages ship without one
     * today, and defaulting to `'write'` would silently change what a
     * read-only user can reach.
     */
    requires?: WriteCapabilityLevel;
    disabled?: boolean;
    /**
     * Tooltip explaining why it is disabled. Needed because a disabled Button
     * swallows its own tooltip (`disabled:pointer-events-none`), which pages
     * used to work around with a wrapping `<span title>`.
     */
    disabledReason?: string;
    /** DOM id — onboarding tours anchor on these. Always forward it. */
    id?: string;
}

export interface CreateActionsProps {
    actions: CreateAction[];
    /**
     * `md` (default) matches the h-10 of every toolbar control.
     * `lg` matches the h-14 of a PageHeader row — PageHeader passes this
     * itself, so pages should not need to.
     */
    size?: 'md' | 'lg';
    className?: string;
}

const CreateActions: React.FC<CreateActionsProps> = ({ actions, size = 'md', className = '' }) => {
    const isMobile = useIsMobile();
    if (actions.length === 0) return null;

    if (!isMobile) {
        return (
            <>
                {actions.map((a, i) => (
                    <Button
                        key={a.id ?? `${a.label}-${i}`}
                        id={a.id}
                        variant="primary"
                        size={size}
                        icon={a.icon}
                        requires={a.requires}
                        disabled={a.disabled}
                        title={a.disabled ? a.disabledReason : undefined}
                        onClick={a.onClick}
                        className={className}
                    >
                        {a.label}
                    </Button>
                ))}
            </>
        );
    }

    // One action: a round icon button. No menu — a menu holding a single item
    // is an extra tap for nothing.
    // On mobile the round button is ALWAYS the compact one. A PageHeader
    // create (`size="lg"`) was rendering a 56px circle beside 40px controls,
    // which read as a different class of button rather than the same one.
    const mobileSize = 'md' as const;

    const single = actions.length === 1 ? actions[0] : undefined;
    if (single) {
        const a = single;
        return (
            <Button
                id={a.id}
                variant="primary"
                size={mobileSize}
                icon={a.icon}
                iconOnly
                aria-label={a.label}
                requires={a.requires}
                disabled={a.disabled}
                title={a.disabled ? a.disabledReason : a.label}
                onClick={a.onClick}
                className={`!rounded-full ${className}`}
            />
        );
    }

    // Two or more: one `+` opening the shared menu. The creates of a page are
    // peers — neither is subordinate — so neither may be demoted to a
    // secondary button.
    return (
        <OverflowMenu
            ariaLabel={actions[0]?.label ?? 'crear'}
            className={className}
            items={actions.map((a, i) => ({
                key: a.id ?? `${a.label}-${i}`,
                icon: a.icon,
                label: a.label,
                onClick: a.onClick,
                disabled: a.disabled,
                title: a.disabled ? a.disabledReason : undefined,
            }))}
            renderTrigger={({ open }) => (
                <span
                    className={`inline-flex items-center justify-center w-10 h-10 rounded-full bg-primary text-white shadow-lg shadow-primary/20 transition-transform ${
                        open ? 'rotate-45' : ''
                    }`}
                >
                    <Icon name="add" className="!text-[22px]" />
                </span>
            )}
        />
    );
};

export default CreateActions;
